"use client"

import { useState, useEffect } from "react"
import {
  Modal, Box, Typography, TextField, Button, RadioGroup,
  FormControlLabel, Radio, IconButton, Grid, Autocomplete,
  CircularProgress, Snackbar, Alert as MuiAlert,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import axios from "axios"
import BaseURL from "../../api/AppURL"

const style = {
  position: "absolute",
  top: "50%", left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "98%", sm: "95%", md: "85%", lg: "75%" },
  maxWidth: 1200, bgcolor: "background.paper",
  borderRadius: 2, boxShadow: 24,
  p: { xs: 2, sm: 4 },
  maxHeight: "95vh", overflowY: "auto",
}

export default function EditPaymentModal({ open, onClose, paymentId, onUpdateSuccess }) {
  // All states ...
  const [loading, setLoading] = useState(false)
  const [paymentMode, setPaymentMode] = useState("")
  const [customers, setCustomers] = useState([])
  const [customer, setCustomer] = useState(null)
  const [formData, setFormData] = useState({
    mobile_no: "", address: "",
    amount: "", dueamount: "", dueafterpay: "", duebeforepay: "",
    paymentDate: "", netAmount: "", comment: "", paymentMode: ""
  })
  const [amount, setAmount] = useState("")
  const [comment, setComment] = useState("")
  const [netAmount, setNetAmount] = useState("")
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMsg, setAlertMsg] = useState("")
  const [alertType, setAlertType] = useState("error")
  const [saving, setSaving] = useState(false)
  const [bankAccounts, setBankAccounts] = useState([])
  const [selectedBankAccount, setSelectedBankAccount] = useState(null)
  const [bankAccountName, setBankAccountName] = useState("")
  const [checkNumber, setCheckNumber] = useState("")
  const [senderMobileNo, setSenderMobileNo] = useState("")
  const [transactionId, setTransactionId] = useState("")
  const [expenses, setExpenses] = useState([])
  const [selectedExpense, setSelectedExpense] = useState(null)
  const [expenseName, setExpenseName] = useState("")
  const [trCharge, setTrCharge] = useState("")
  const [dueAfterPayment, setDueAfterPayment] = useState("");
  const [showDueAfterPayment, setShowDueAfterPayment] = useState(false);

  // 1. On modal open, first fetchData, then fetchPaymentDetails
  useEffect(() => {
    if (open && paymentId) {
      const loadData = async () => {
        await fetchData();
        await fetchPaymentDetails();
      };
      loadData();
    }
    // eslint-disable-next-line
  }, [open, paymentId]);

  // 2. fetchData: Expenses, BankAccounts, Customers
  const fetchData = async () => {
    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.get(`${BaseURL}/payment/add`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      setCustomers(response.data.customers || [])
      setBankAccounts(response.data.bankaccounts || [])
      setExpenses(response.data.expensetype || [])
    } catch (err) {
      console.error("Error fetching data:", err)
    }
  }

  // 3. fetchPaymentDetails: Set all fields (after bankAccounts/expenses loaded)
  const fetchPaymentDetails = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem("auth_token")
      const res = await axios.get(`${BaseURL}/get/paymentdetails/${paymentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      setShowDueAfterPayment(false);
      setDueAfterPayment("");
      setCustomer(res.data.customer)
      const pmode = res.data.payment.payment_mode
      setPaymentMode(pmode)
      setFormData({
        paymentDate: res.data.payment.payment_date || "",
        netAmount: res.data.payment.amount || "",
        paymentMode: pmode,
        comment: res.data.payment.comment || "",
        mobile_no: res.data.customer?.mobile_no || "",
        address: res.data.customer?.address || "",
        dueamount: res.data.total_due || "",
        duebeforepay: res.data.payment.due_before_payment || "",
      })
      setComment(res.data.payment.comment || "")

      // Amount
      if (["Bkash", "BankTR", "Rocket", "Upay", "Nagad"].includes(pmode)) {
        setAmount(res.data.payment.prev_amount)
      } else {
        setAmount(res.data.payment.amount)
      }

      // --- Store names, not selected objects! ---
      if (pmode === "BankTR") {
        setBankAccountName(res.data.payment.btraccount_no || "")
        setTrCharge(res.data.payment.btr_cost || "")
        setExpenseName("banktr")
      }
      if (pmode === "Bkash") {
        setSenderMobileNo(res.data.payment.bkash_number || "")
        setTransactionId(res.data.payment.tranx_number || "")
        setExpenseName("bkash")
      }
      if (pmode === "Nagad") {
        setSenderMobileNo(res.data.payment.nagad_number || "")
        setTransactionId(res.data.payment.ntranx_number || "")
        setExpenseName("nagad")
      }
      if (pmode === "Rocket") {
        setSenderMobileNo(res.data.payment.rocket_number || "")
        setTransactionId(res.data.payment.rtranx_number || "")
        setExpenseName("rocket")
      }
      if (pmode === "Upay") {
        setSenderMobileNo(res.data.payment.upay_number || "")
        setTransactionId(res.data.payment.utranx_number || "")
        setExpenseName("upay")
      }
      if (pmode === "Check") {
        setBankAccountName(res.data.payment.account_no || "")
        setCheckNumber(res.data.payment.check_no || "")
      }
    } catch (err) {
      showAlert("Failed to load payment details.", "error")
    } finally {
      setLoading(false)
    }
  }


  const handleUpdatePayment = async () => {

    if (
      !formData.paymentDate ||
      !paymentMode
    ) {
      showAlert("Please fill all required fields.", "error")
      return
    }
    setSaving(true)
    try {
      const token = localStorage.getItem("auth_token")
      // Prepare arrays for API
      const payload = {
        editpayment_date: formData.paymentDate,
        editpayment_mode: paymentMode,
        editprev_amount: amount,
        editamount: netAmount,
        editcomment: comment,

      }

      // Payment mode specific fields
      if (paymentMode === "Check") {
        payload.editbank_account_number = selectedBankAccount?.id;
        payload.editcheck_number = checkNumber;
      }
      if (paymentMode === "Bkash") {
        payload.editbkash_number = senderMobileNo;
        payload.edittranx_number = transactionId;
        if (selectedExpense?.id) payload.editexpense_typeb = selectedExpense.id;
      }
      if (paymentMode === "Nagad") {
        payload.editnagad_number = senderMobileNo;
        payload.editntranx_number = transactionId;
        if (selectedExpense?.id) payload.editexpense_typen = selectedExpense.id;
      }
      if (paymentMode === "Rocket") {
        payload.editrocket_number = senderMobileNo;
        payload.editrtranx_number = transactionId;
        if (selectedExpense?.id) payload.editexpense_typer = selectedExpense.id;
      }
      if (paymentMode === "Upay") {
        payload.editupay_number = senderMobileNo;
        payload.editutranx_number = transactionId;
        if (selectedExpense?.id) payload.editexpense_typeu = selectedExpense.id;
      }
      if (paymentMode === "BankTR") {
        payload.editbank_account_numbertr = selectedBankAccount?.id;
        payload.edittr_charge = trCharge;
        if (selectedExpense?.id) payload.expense_typebtr = selectedExpense.id;
      }
      // Use PUT method and id in url
      const res = await axios.put(`${BaseURL}/payment/update/${paymentId}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      if (res.data.success || res.data.message?.includes("success")) {
        // showAlert("Payment updated successfully!", "success")
        if (onUpdateSuccess) onUpdateSuccess("Payment updated successfully!")
        setTimeout(() => {
          onClose()
        }, 800)
      } else {
        showAlert(res.data.message || "Failed to update payment.", "error")
      }
    } catch (err) {
      showAlert("Failed to update payment.", "error")
    } finally {
      setSaving(false)
    }
  }

  // Bank Receive Account: set selectedBankAccount after bankAccounts and bankAccountName are loaded
  useEffect(() => {
    if (paymentMode === "BankTR" && bankAccountName && bankAccounts.length > 0) {
      const selectedBank = bankAccounts.find(
        b => b.bank_account === bankAccountName
      );
      setSelectedBankAccount(selectedBank || null);
    }
    if (paymentMode === "Check" && bankAccountName && bankAccounts.length > 0) {
      const selectedBank = bankAccounts.find(
        b => b.bank_account === bankAccountName
      );
      setSelectedBankAccount(selectedBank || null);
    }
  }, [bankAccounts, bankAccountName, paymentMode]);

  // Expense: set selectedExpense after expenses and expenseName are loaded
  useEffect(() => {
    if (
      ["BankTR", "Bkash", "Nagad", "Rocket", "Upay"].includes(paymentMode) &&
      expenseName &&
      expenses.length > 0
    ) {
      const foundExpense = expenses.find(
        ex =>
          ex.edetailsname &&
          ex.edetailsname.toLowerCase().includes(expenseName.toLowerCase())
      );
      setSelectedExpense(foundExpense || null);
    }
    if (
      !["BankTR", "Bkash", "Nagad", "Rocket", "Upay"].includes(paymentMode)
    ) {
      setSelectedExpense(null);
    }
  }, [expenses, expenseName, paymentMode]);

  // Net Amount calculation
  useEffect(() => {
    let amt = Number(amount) || 0;
    let tr = Number(trCharge) || 0;
    let net = amt;
    if (paymentMode === "Bkash") net = amt - Math.floor(amt / 500) * 9;
    else if (paymentMode === "Nagad") net = amt - Math.floor(amt / 500) * 8;
    else if (paymentMode === "Rocket") net = amt - Math.floor(amt / 500) * 7;
    else if (paymentMode === "Upay") net = amt - Math.floor(amt / 500) * 6;
    else if (paymentMode === "BankTR") net = amt - tr;
    setNetAmount(net > 0 ? net : "");
  }, [amount, paymentMode, trCharge]);




  function parseDueAmount(val) {
    if (typeof val === "string" && val.endsWith("cr")) {
      return -Number(val.replace("cr", ""))
    }
    return Number(val) || 0
  }
  function formatDueAfterPayment(due, amount) {
    const dueNum = parseDueAmount(due)
    const amtNum = Number(amount) || 0
    const result = dueNum - amtNum
    if (amtNum === 0) return ""
    if (result < 0) return `${Math.abs(result)}cr`
    if (result > 0) return result
    return 0
  }

  useEffect(() => {
    if (!showDueAfterPayment) {
      setDueAfterPayment("");
      return;
    }
    const dueNum = parseDueAmount(formData.duebeforepay);
    const amtNum = Number(amount) || 0;
    const result = dueNum - amtNum;
    if (amtNum === 0) setDueAfterPayment("");
    else if (result < 0) setDueAfterPayment(`${Math.abs(result)}cr`);
    else if (result > 0) setDueAfterPayment(result);
    else setDueAfterPayment(0);
  }, [amount, formData.duebeforepay, showDueAfterPayment]);

  const showAlert = (msg, type = "error") => {
    setAlertMsg(msg)
    setAlertType(type)
    setAlertOpen(true)
  }


  const formatDueAmount = (value) => {
    if (value === undefined || value === null || value === "") return ""
    const num = Number(value)
    if (isNaN(num)) return ""
    if (num < 0) return `${Math.abs(num)}cr`
    return num
  }

  // Payment mode conditional fields
  const renderPaymentModeFields = () => {
    if (paymentMode === "Check") {
      return (
        <>
          <Autocomplete
            options={bankAccounts}
            getOptionLabel={option => option.bank_account || ""}
            value={selectedBankAccount}
            onChange={(e, v) => setSelectedBankAccount(v)}
            renderInput={params => <TextField {...params} label="Bank Receive Account" sx={{ mb: 2 }} />}
          />
          <TextField
            fullWidth
            label="Check Number"
            value={checkNumber}
            onChange={e => setCheckNumber(e.target.value)}
            sx={{ mb: 2 }}
          />
        </>
      )
    }
    if (["Bkash", "Nagad", "Rocket", "Upay"].includes(paymentMode)) {
      let paymentLabel = ""
      if (paymentMode === "Bkash") paymentLabel = "Bkash Sender Mobile No"
      else if (paymentMode === "Nagad") paymentLabel = "Nagad Sender Mobile No"
      else if (paymentMode === "Rocket") paymentLabel = "Rocket Sender Mobile No"
      else if (paymentMode === "Upay") paymentLabel = "Upay Sender Mobile No"
      return (
        <>
          <TextField
            fullWidth
            label={paymentLabel}
            value={senderMobileNo}
            onChange={e => setSenderMobileNo(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Transaction ID"
            value={transactionId}
            onChange={e => setTransactionId(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Autocomplete
            options={expenses}
            getOptionLabel={option => option.edetailsname || ""}
            value={selectedExpense}
            onChange={(e, v) => setSelectedExpense(v)}
            renderInput={params => <TextField {...params} label="Select Expense" sx={{ mb: 2 }} />}
          />
        </>
      )
    }
    if (paymentMode === "BankTR") {
      return (
        <>
          <Autocomplete
            options={bankAccounts}
            getOptionLabel={option => option.bank_account || ""}
            value={selectedBankAccount}
            onChange={(e, v) => setSelectedBankAccount(v)}
            renderInput={params => <TextField {...params} label="Bank Receive Account" sx={{ mb: 2 }} />}
          />
          <Autocomplete
            options={expenses}
            getOptionLabel={option => option.edetailsname || ""}
            value={selectedExpense}
            onChange={(e, v) => setSelectedExpense(v)}
            renderInput={params => <TextField {...params} label="Select Expense" sx={{ mb: 2 }} />}
          />
          <TextField
            fullWidth
            label="TR Charge"
            value={trCharge}
            onChange={e => setTrCharge(e.target.value.replace(/[^0-9.]/g, ""))}
            sx={{ mb: 2 }}
          />
        </>
      )
    }
    return null
  }

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={style}>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5" fontWeight={600}>
              Edit Payment
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid
                container
                spacing={4}
                sx={{
                  flexWrap: { xs: "wrap", md: "nowrap" },
                  "@media (max-width:1265px) and (min-width:1200px)": { flexWrap: "nowrap" },
                }}
              >
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Customer"
                    value={customer?.name || ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    value={formData.mobile_no}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Address"
                    value={formData.address}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Due Amount"
                    value={formatDueAmount(formData.dueamount)}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Due After Payment"
                    value={dueAfterPayment}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Due Before Payment"
                    value={formData.duebeforepay}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Payment Date"
                    type="date"
                    value={formData.paymentDate}
                    onChange={e => setFormData(prev => ({ ...prev, paymentDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Amount"
                    value={amount}
                    onChange={e => {
                      setAmount(e.target.value);
                      setShowDueAfterPayment(true); // User input দিলে true হবে
                    }}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ mb: 1 }}>Payment Mode</Typography>
                    <RadioGroup
                      row
                      value={paymentMode}
                      onChange={e => {
                        const newMode = e.target.value;
                        setPaymentMode(newMode);
                        // Payment Mode অনুযায়ী expenseName সেট করো
                        if (["Bkash", "Nagad", "Rocket", "Upay", "BankTR"].includes(newMode)) {
                          setExpenseName(newMode.toLowerCase());
                        } else {
                          setExpenseName("");
                          setSelectedExpense(null); // অন্য কোনো mode হলে expense সাফ
                        }
                      }}
                    >
                      <FormControlLabel value="Cash" control={<Radio />} label="Cash" />
                      <FormControlLabel value="Card" control={<Radio />} label="Card" />
                      <FormControlLabel value="Check" control={<Radio />} label="Check" />
                      <FormControlLabel value="Bkash" control={<Radio />} label="Bkash" />
                      <FormControlLabel value="Nagad" control={<Radio />} label="Nagad" />
                      <FormControlLabel value="Rocket" control={<Radio />} label="Rocket" />
                      <FormControlLabel value="Upay" control={<Radio />} label="Upay" />
                      <FormControlLabel value="BankTR" control={<Radio />} label="Bank TR" />
                    </RadioGroup>
                  </Box>
                  {renderPaymentModeFields()}
                  <TextField
                    fullWidth
                    label="Net Amount"
                    value={netAmount}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Comment"
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>
              {/* Footer */}
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#04b6e0", color: "white", px: 2, py: 0.5,
                    fontSize: 18, fontWeight: 550, borderRadius: 1,
                    "&:hover": { bgcolor: "#0299be" },
                  }}
                  onClick={handleUpdatePayment}
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={24} color="inherit" /> : "Update"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
      {/* Snackbar Alert */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={3000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setAlertOpen(false)}
          severity={alertType}
          elevation={6}
          variant="filled"
          sx={{ fontWeight: 600, fontSize: 16 }}
        >
          {alertMsg}
        </MuiAlert>
      </Snackbar>
    </>
  )
}